// Transport (SP1) — <bw-transport> wired to player.ts. View-only: play/stop/
// seek + master volume. No record / fade / bar-counter actions on web.

import '@bandway/ui-components';
import type { Player } from '../player.js';
import { t } from '../ui/i18n.js';

export interface TransportDeps {
  player: Player & { setMasterGain?: (g: number) => void };
  durationSec: number;
  /** Called when the (view-only) record button is clicked → desktop promo. */
  onRecordAttempt?: () => void;
  /** Setlist navigation — enable + wire the prev/next transport buttons. When a
   *  handler is absent the corresponding button stays disabled (single song or
   *  edge of the list). */
  hasPrev?: boolean;
  hasNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  /** Mobile background audio: starts playback via the orchestrator so the
   *  background <audio> begins INSIDE the Play tap's user gesture (the only
   *  moment iOS permits it). When absent, playback starts via player.play(). */
  onPlayGesture?: () => void;
  /** Стартовый Master (множитель, 1 = 0 dB) — когда экран пересобран на повороте, а плеер живёт
   *  дальше (view-memo.ts). По умолчанию 1. */
  initialMaster?: number;
  /** Master сменился ручкой (input и change) — уже зажатый в 0..4 множитель, тот же, что ушёл в
   *  движок. Через него широкая раскладка помнит Master для телефонного экрана. */
  onMasterChange?: (factor: number) => void;
}

export interface TransportHandle {
  unmount: () => void;
  /** Called by the rAF loop to push playhead + playing state into the component. */
  syncTime: (currentSec: number, playing: boolean, masterVu: number) => void;
}

const fmt = (sec: number): string => {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export function mountTransport(host: Element, deps: TransportDeps): TransportHandle {
  const { player, durationSec } = deps;
  const el = document.createElement('bw-transport') as HTMLElement & {
    mode: string; playing: boolean; loading: boolean;
    time: { current?: number; total?: number; barCounter?: string };
    masterVolume: number; masterVu: number;
    hasPrev: boolean; hasNext: boolean;
  };
  el.mode = 'rehearsal';
  el.playing = false;
  el.time = { current: 0, total: durationSec, barCounter: fmt(0) };
  el.masterVolume = deps.initialMaster ?? 1;
  el.masterVu = 0;
  // Setlist prev/next: enable only when the boot orchestrator supplied a handler
  // (i.e. there IS an adjacent song). bw-transport disables the button on
  // has-prev/has-next=false, so this doubles as the disabled gate.
  el.hasPrev = !!deps.hasPrev;
  el.hasNext = !!deps.hasNext;

  // ── Loading state (B-260621) ─────────────────────────────────────────────
  // Until the song's audio is decoded, player.play() throws — the old handler
  // did `void player.play()`, silently swallowing that throw, so a Play tap did
  // nothing with no UI feedback. Drive the bw-play-button spinner off the
  // player's load state, and remember a tap made while loading so it auto-starts
  // the moment the song is ready. (Some test mocks omit these methods — guard.)
  const isReady = (): boolean =>
    typeof player.isLoaded === 'function' ? player.isLoaded() : true;
  let pendingPlay = false;
  el.loading = !isReady();

  // Start playback. On mobile this goes through the background orchestrator's
  // gesture entry (starts the <audio> inside the tap); otherwise straight to the
  // live player. Called from the Play tap (gesture-preserving) and from the
  // auto-start after load (pendingPlay — no gesture; mobile <audio> may not start
  // there, a known tap-while-loading limitation).
  const startPlayback = (): void => {
    if (deps.onPlayGesture) deps.onPlayGesture();
    else void player.play();
  };

  // ── Load progress indicator (B-260622) ──────────────────────────────────
  // Shows "X/N tracks" while stems are downloading/decoding; hidden once the
  // player emits `loaded`. Lives in `host` (not inside bw-transport's shadow
  // DOM) so it's a plain, easily-tested/-styled sibling of the transport bar.
  const progress = document.createElement('div');
  progress.className = 'tr-load-progress';
  progress.hidden = true;

  let unsubLoad: () => void = () => {};
  if (typeof player.subscribe === 'function') {
    unsubLoad = player.subscribe((ev) => {
      if (ev.type === 'load-progress') {
        progress.hidden = false;
        progress.textContent = t('player.loadingTracks', { current: ev.current, total: ev.total });
        return;
      }
      if (ev.type !== 'loaded') return;
      progress.hidden = true;
      progress.textContent = '';
      el.loading = false;
      if (pendingPlay) { pendingPlay = false; startPlayback(); }
    });
  }

  // bw-play-button is the single play/pause control; bw-transport emits
  // `bw-play` for both states (state-dumb). Toggle here — pause when playing,
  // play when paused — mirroring desktop stage-view's togglePlay(). Without
  // this the Pause-icon tap fell through to play() → `if (playing) return`
  // no-op, so playback could be started but never paused (B-104 follow-up).
  const onPlay = () => {
    if (!isReady()) { pendingPlay = true; el.loading = true; return; }
    if (player.isPlaying()) player.pause();
    else startPlayback();
  };
  const onStop = () => { player.pause(); player.seek(0); };
  const onMasterInput = (e: Event) => {
    const v = (e as CustomEvent<{ value: number }>).detail.value;
    const factor = Math.min(4, Math.max(0, v));
    player.setMasterGain?.(factor);
    deps.onMasterChange?.(factor);
  };
  const onMasterChange = onMasterInput;

  el.addEventListener('bw-play', onPlay);
  el.addEventListener('bw-stop', onStop);
  el.addEventListener('bw-master-volume-input', onMasterInput);
  el.addEventListener('bw-master-volume-change', onMasterChange);
  const onRec = (): void => { deps.onRecordAttempt?.(); };
  el.addEventListener('bw-rec', onRec);
  const onPrev = (): void => { deps.onPrev?.(); };
  const onNext = (): void => { deps.onNext?.(); };
  el.addEventListener('bw-prev', onPrev);
  el.addEventListener('bw-next', onNext);

  host.appendChild(el);
  host.appendChild(progress);

  return {
    unmount: () => {
      unsubLoad();
      el.removeEventListener('bw-play', onPlay);
      el.removeEventListener('bw-stop', onStop);
      el.removeEventListener('bw-master-volume-input', onMasterInput);
      el.removeEventListener('bw-master-volume-change', onMasterChange);
      el.removeEventListener('bw-rec', onRec);
      el.removeEventListener('bw-prev', onPrev);
      el.removeEventListener('bw-next', onNext);
      try { host.removeChild(el); } catch { /* noop */ }
      try { host.removeChild(progress); } catch { /* noop */ }
    },
    syncTime: (currentSec, playing, masterVu) => {
      el.time = { current: currentSec, total: durationSec, barCounter: fmt(currentSec) };
      el.playing = playing;
      el.masterVu = masterVu;
    },
  };
}
